<script>
import api from '@utils/api'
import { createBarcodeScanner, isMobile } from '@utils/barcode/barcode-scanner'
import { sortBy } from 'lodash'

export default {
  name: 'NewMergeSpecification',
  props: {
    newSpecification: {
      type: Boolean,
      default: false,
    },
    currentDocument: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
      valid: true,
      document: this.$route.params.document,
      newItem: {
        document: this.$route.params.document.id,
        goods: null,
        serial: null,
        unit: 104,
        quantity: 1,
      },
      rules: {
        goods: [x => !!x || this.$t('validation__is_required')],
        serial: [x => !!x || this.$t('validation__is_required')],
      },
      serialNumbers: [],
      serialNumbersLoading: false,
      scannerIsVisible: false,
      barcodeScanner: createBarcodeScanner(document),
      isMobile: isMobile(),
    }
  },
  computed: {
    isSelectedSerial() {
      return (
        this.document.type === 2 ||
        this.document.type === 4 ||
        this.document.type === 5
      )
    },
    goods() {
      const goods = this.$store.getters['data/getGoods'](this.document.type)
      return goods.map(item => {
        return {
          id: item.id,
          shortName: `[${item.articleNumber}] ${item.shortName}`,
        }
      })
    },
    currentGoods() {
      return this.newItem.goods
    },
    hasSerials() {
      if (!this.serialNumbers) return false
      if (!this.serialNumbers.length) return false
      return true
    },
  },
  watch: {
    newSpecification(v) {
      if (v) {
        this.getSerials()
      }
    },
  },
  methods: {
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    clearItem() {
      this.newItem.goods = null
      this.newItem.serial = null
      this.serialNumbers = []
      this.valid = true
    },
    submit() {
      let selectedArticle = this.newItem.goods
      if (this.$refs.newSpecificationForm.validate()) {
        this.loading = true
        api.createSpecification(this.newItem).then(response => {
          this.clearItem()
          this.$emit('spec-added')
          this.$refs.newSpecificationForm.reset()
          this.newItem.goods = selectedArticle
          this.getSerials()
          this.loading = false
        })
      }
    },
    closeModal() {
      this.$refs.newSpecificationForm.reset()
      this.serialNumbers = []
      this.$emit('close-modal')
    },
    getSerials() {
      this.serialNumbersLoading = true
      let plug = []
      api
        .getSerialNumbers(this.currentDocument.fromWarehouse, '2')
        .then(response => {
          plug = response
          api
            .getSerialNumbers(
              this.currentDocument.fromWarehouse,
              '2,5',
              null,
              true
            )
            .then(response => {
              const pdo = response
              this.serialNumbers = sortBy(
                plug.filter(item => !pdo.includes(item))
              )
              this.serialNumbersLoading = false
            })
        })
    },
    openBarcodeScanner(field, trasformValueFn) {
      this.scannerIsVisible = true
      this.barcodeScanner.openScannerForField(
        value =>
          this.setFieldValue(
            field,
            trasformValueFn ? trasformValueFn(value) : value
          ),
        () => this.closeScanner()
      )
    },
    setFieldValue(field, value) {
      this.newItem[field] = value
    },
    findGoodsById(itemId) {
      let filteredById = this.goods.filter(
        goodsItem => goodsItem.id === parseInt(itemId)
      )
      if (filteredById && filteredById.length > 0) return filteredById[0]
      else
        this.$store.commit('notifications/ADD', {
          type: 'warning',
          message: this.$t('error__article_not_found'),
        })
    },
    closeScanner() {
      this.barcodeScanner.closeScanner()
      this.scannerIsVisible = false
    },
  },
}
</script>

<template>
  <v-dialog
    :value="newSpecification"
    class="position-relative"
    persistent
    max-width="500px"
  >
    <v-card>
      <v-card-title>
        <v-spacer />
        <div class="headline">{{ $t('titles__new_specification') }}</div>
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12>
              <v-form
                ref="newSpecificationForm"
                v-model="valid"
                lazy-validation
              >
                <v-text-field
                  :label="$t('label__document')"
                  :value="document.number"
                  name="document"
                  readonly
                />
                <v-layout 
                  row 
                  align-center>
                  <v-flex>
                    <v-select
                      :items="goods"
                      :label="$t('label__article')"
                      v-model="newItem.goods"
                      :rules="rules.goods"
                      item-text="shortName"
                      item-value="id"
                      required
                      autocomplete
                    />
                  </v-flex>
                  <v-flex 
                    v-if="isMobile" 
                    shrink>
                    <img
                      class="barcodeScanIcon"
                      src="@assets/images/barcode_scan_icon.png"
                      @click="openBarcodeScanner('goods', findGoodsById)"
                    >
                  </v-flex>
                </v-layout>
                <v-layout 
                  row 
                  align-center>
                  <v-flex>
                    <v-select
                      :items="serialNumbers"
                      :label="$t('label__serial')"
                      v-model="newItem.serial"
                      :rules="rules.serial"
                      :loading="serialNumbersLoading"
                      required
                      autocomplete
                    />
                  </v-flex>
                  <v-flex 
                    v-if="isMobile" 
                    shrink>
                    <img
                      class="barcodeScanIcon"
                      src="@assets/images/barcode_scan_icon.png"
                      @click="openBarcodeScanner('serial')"
                    >
                  </v-flex>
                </v-layout>
                <v-text-field
                  :label="$t('label__unit')"
                  :value="getValue('unit', newItem.unit)"
                  name="uni"
                  readonly
                />
                <v-text-field
                  :label="$t('label__quantity')"
                  :value="newItem.quantity"
                  name="quantity"
                  readonly
                />
              </v-form>
              <small>* {{ $t('label__requier_small_text') }}</small>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          :disabled="loading"
          color="secondary"
          @click="closeModal">{{ $t('buttons__close') }}</v-btn>
        <v-btn
          :disabled="loading"
          :loading="loading"
          color="primary"
          @click="submit">{{ $t('buttons__create') }}</v-btn>
      </v-card-actions>
    </v-card>
    <v-card
      v-show="scannerIsVisible"
      id="scanner-container"
      class="scannerContainer">
      <v-btn
        class="scannerCancelBtn"
        color="secondary"
        @click="closeScanner()"
      >
        {{ $t('buttons__cancel') }}
      </v-btn>
    </v-card>
    <div 
      v-if="scannerIsVisible"
      class="blurred"/>
  </v-dialog>
</template>

<style lang="scss" module>
@import '@design';
.warningMsg {
  color: #ff5252;
}
.relativePosition {
  position: relative;
}
</style>
